import React, { useState } from 'react';
import { DurationChips } from '../components/DurationChips';
import { Toggle } from '../components/Toggle';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { DURATION_PRESETS, GOAL_PRESETS, MODE_LABELS } from '../data/presets';
import { Mode, ThemePreference } from '../types/pomodoro';
import { usePomodoro } from '../contexts/PomodoroContext';
const THEMES: {
  id: ThemePreference;
  label: string;
}[] = [{
  id: 'light',
  label: 'Light'
}, {
  id: 'dark',
  label: 'Dark'
}, {
  id: 'system',
  label: 'System'
}];
function Section({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <section className="border-t border-line py-6 first:border-t-0 first:pt-0" data-id="element-127" data-mp="h2bzr4hnuech">
      <h2 className="text-xs font-medium uppercase tracking-wide text-muted" data-id="element-128" data-mp="m0n6h6cns8qh">{title}</h2>
      <div className="mt-3" data-id="element-129" data-mp="2bftnoslz9d81">{children}</div>
    </section>;
}
export function SettingsScreen() {
  const {
    settings,
    updateSettings,
    setDuration,
    clearHistory,
    sessions,
    timer,
    sessionInProgress
  } = usePomodoro();
  const [confirmClear, setConfirmClear] = useState(false);
  const [notifyNote, setNotifyNote] = useState<string | null>(null);
  const [customGoal, setCustomGoal] = useState('');
  const [pending, setPending] = useState<{
    mode: Mode;
    minutes: number;
  } | null>(null);

  /** Changing the duration of the mode that is mid-session must never discard progress silently. */
  const requestDuration = (mode: Mode, minutes: number) => {
    if (sessionInProgress && mode === timer.mode) {
      setPending({
        mode,
        minutes
      });
      return;
    }
    setDuration(mode, minutes);
  };
  const handleNotifications = async (next: boolean) => {
    if (!next) {
      updateSettings({
        notifications: false
      });
      setNotifyNote(null);
      return;
    }
    if (typeof Notification === 'undefined') {
      setNotifyNote('This browser does not support notifications.');
      return;
    }
    try {
      const permission = Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission();
      if (permission === 'granted') {
        updateSettings({
          notifications: true
        });
        setNotifyNote('Notifications arrive only while this tab is open.');
      } else {
        updateSettings({
          notifications: false
        });
        setNotifyNote('Notifications are blocked in your browser settings.');
      }
    } catch {
      updateSettings({
        notifications: false
      });
      setNotifyNote('Notifications could not be enabled.');
    }
  };
  return <div className="flex min-h-full flex-col px-5 pb-24 pt-[max(1rem,env(safe-area-inset-top))]" data-id="element-130" data-mp="6yymf5uhjjwr">
      <header data-id="element-131" data-mp="byl0fhi5146m">
        <h1 className="text-sm font-semibold tracking-tight text-ink" data-id="element-132" data-mp="by2utu9l5dm1">Settings</h1>
      </header>

      <div className="mx-auto mt-6 w-full max-w-sm" data-id="element-133" data-mp="gpb5suqkd71b">
        {(['focus', 'short', 'long'] as Mode[]).map(mode => <Section key={mode} title={`${MODE_LABELS[mode]} duration`} data-id="element-134" data-mp="3fea1m9narqz1">
            <DurationChips presets={DURATION_PRESETS[mode]} value={settings.durations[mode]} onSelect={minutes => requestDuration(mode, minutes)} idPrefix={`settings-${mode}`} data-id="element-135" data-mp="3kdmj0eveg5u8" />
          </Section>)}

        <Section title="Long break after" data-id="element-136" data-mp="32wfo45wf4og9">
          <div className="flex gap-2" data-id="element-137" data-mp="32v4lwaimfor9">
            {([2, 3, 4] as const).map(count => {
            const active = settings.longBreakInterval === count;
            return <button key={count} type="button" aria-pressed={active} onClick={() => updateSettings({
              longBreakInterval: count
            })} className={`min-h-[44px] flex-1 rounded-xl border text-sm font-medium transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-coral ${active ? 'border-coral bg-coral text-white' : 'border-line text-ink hover:border-muted'}`} data-id="element-138" data-mp="2tw0zawk8wr80">
                  {count} sessions
                </button>;
          })}
          </div>
        </Section>

        <Section title="Daily focus goal" data-id="element-139" data-mp="e4voonolgq4e">
          <div className="flex flex-wrap gap-2" data-id="element-140" data-mp="e6q6vu8he44s">
            {GOAL_PRESETS.map(minutes => {
            const active = settings.dailyGoal === minutes;
            return <button key={minutes} type="button" aria-pressed={active} onClick={() => updateSettings({
              dailyGoal: minutes
            })} className={`min-h-[44px] min-w-[68px] rounded-xl border px-3 text-sm font-medium transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-coral ${active ? 'border-coral bg-coral text-white' : 'border-line text-ink hover:border-muted'}`} data-id="element-141" data-mp="5ar0yd4rbut3">
                  {minutes} min
                </button>;
          })}
            {settings.dailyGoal !== null && !GOAL_PRESETS.includes(settings.dailyGoal) && <span className="flex min-h-[44px] items-center rounded-xl border border-coral bg-coral px-3 text-sm font-medium text-white" data-id="element-142" data-mp="1lx3ze4dnswcm">
                {settings.dailyGoal} min
              </span>}
            <button type="button" aria-pressed={settings.dailyGoal === null} onClick={() => updateSettings({
            dailyGoal: null
          })} className={`min-h-[44px] rounded-xl border px-3 text-sm font-medium transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-coral ${settings.dailyGoal === null ? 'border-ink bg-ink text-bg' : 'border-line text-ink hover:border-muted'}`} data-id="element-143" data-mp="1uvy1295ukcx1">
              Hidden
            </button>
          </div>
          <form onSubmit={e => {
          e.preventDefault();
          const parsed = Number.parseInt(customGoal, 10);
          if (!Number.isFinite(parsed)) return;
          updateSettings({
            dailyGoal: Math.min(960, Math.max(5, parsed))
          });
          setCustomGoal('');
        }} className="mt-3 flex items-center gap-2" data-id="element-144" data-mp="k91xe12jparr">
            <label htmlFor="custom-goal" className="sr-only" data-id="element-145" data-mp="2yaav8u565o5t">
              Custom daily goal in minutes
            </label>
            <input id="custom-goal" type="number" inputMode="numeric" min={5} max={960} value={customGoal} onChange={e => setCustomGoal(e.target.value)} placeholder="Custom minutes" className="min-h-[44px] flex-1 rounded-xl border border-line bg-bg px-3 text-sm text-ink placeholder:text-muted focus:border-muted focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-coral" data-id="element-146" data-mp="328qmxbc73kpk" />
            <button type="submit" disabled={customGoal.trim() === ''} className="min-h-[44px] rounded-xl border border-line px-4 text-sm font-medium text-ink transition-colors duration-150 hover:bg-bg disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-coral" data-id="element-147" data-mp="1ee508807vnsm">
              Set
            </button>
          </form>
        </Section>

        <Section title="Sessions" data-id="element-148" data-mp="1t1gewirp3nwy">
          <Toggle label="Completion sound" description="A soft chime when a session ends." checked={settings.soundEnabled} onChange={v => updateSettings({
          soundEnabled: v
        })} data-id="element-149" data-mp="1t2i19je5vwtz" />
          <Toggle label="Auto-start breaks" checked={settings.autoStartBreaks} onChange={v => updateSettings({
          autoStartBreaks: v
        })} data-id="element-150" data-mp="2gr0xv1numl68" />
          <Toggle label="Auto-start focus" checked={settings.autoStartFocus} onChange={v => updateSettings({
          autoStartFocus: v
        })} data-id="element-151" data-mp="2byra9sa7hx74" />
        </Section>

        <Section title="Appearance" data-id="element-152" data-mp="1p5khl6zmkfvr">
          <div className="flex gap-2" data-id="element-153" data-mp="1bd49hrrv9rnz">
            {THEMES.map(({
            id,
            label
          }) => {
            const active = settings.theme === id;
            return <button key={id} type="button" aria-pressed={active} onClick={() => updateSettings({
              theme: id
            })} className={`min-h-[44px] flex-1 rounded-xl border text-sm font-medium transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-coral ${active ? 'border-ink bg-ink text-bg' : 'border-line text-ink hover:border-muted'}`} data-id="element-154" data-mp="1fbk5um9u8ce2">
                  {label}
                </button>;
          })}
          </div>
        </Section>

        <Section title="Notifications" data-id="element-155" data-mp="f4587wb2k6kf">
          <Toggle label="Browser notifications" description="Asked for only when you turn this on." checked={settings.notifications} onChange={v => void handleNotifications(v)} data-id="element-156" data-mp="b44r23wl5h9o" />
          <p className="mt-1 text-xs leading-relaxed text-muted" data-id="element-157" data-mp="k5uhp7in8c6e">
            {notifyNote ?? 'Alerts appear while this tab is open. Browsers cannot guarantee alarms once the tab is closed.'}
          </p>
        </Section>

        <Section title="Data" data-id="element-158" data-mp="3njglgukbm3k2">
          <button type="button" onClick={() => setConfirmClear(true)} disabled={sessions.length === 0} className="min-h-[44px] w-full rounded-xl border border-line text-sm font-medium text-coral transition-colors duration-150 hover:bg-coral/10 disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-coral" data-id="element-159" data-mp="64rzj5urrm0r">
            Clear local history
          </button>
          <p className="mt-2 text-xs leading-relaxed text-muted" data-id="element-160" data-mp="22jf82g7h9zdp">
            Everything is stored on this device only — no account, no server.
          </p>
        </Section>
      </div>

      <ConfirmDialog open={pending !== null} title="Replace this session?" description="A session is in progress. Changing this duration restarts it from the beginning." confirmLabel="Replace" onConfirm={() => {
      if (pending) setDuration(pending.mode, pending.minutes);
      setPending(null);
    }} onCancel={() => setPending(null)} data-id="element-161" data-mp="1xmexpt3liags" />

      <ConfirmDialog open={confirmClear} title="Clear all history?" description="Your completed sessions and progress chart will be permanently removed from this device." confirmLabel="Clear" onConfirm={() => {
      clearHistory();
      setConfirmClear(false);
    }} onCancel={() => setConfirmClear(false)} data-id="element-162" data-mp="1erhtwigk2v9f" />
    </div>;
}